// Hierarchical z-score scoring engine. Two stages:
//   1. scoreGroup: within a peer-group (financial/standard/energy/utility/reit),
//      winsorize each factor, z-score, weight-blend per pillar.
//   2. scoreStocks: orchestrates the per-peer-group scoring, then a final
//      cross-pillar blend into qualityScore + valuationScore + momentumScore,
//      again z-scored within the stock's own peer group.

import type { StockData, StockFactors, PillarScores, ScoredStock } from './types';
import {
  isBankLike,
  isInsuranceLike,
  isEnergyLike,
  isUtilityLike,
  isREITLike,
  isTechnologyLike,
  getPeerGroup,
  PeerGroup,
} from './peer-groups';
import { computeStats, zscoreVal, clamp, winsorize } from './scoring-helpers';
import { PILLAR_FACTORS } from './pillar-factors';
import { DEFAULT_PILLAR_WEIGHTS_DECIMAL, QUALITY_PILLAR_KEYS, PillarMapping } from './factor-config';
import { applyFactorOverrides } from './factor-overrides';
import { applyQualityAbsoluteLens } from './quality-absolute-lens';

// Completeness: require at least 1 factor per pillar (keeps newer companies like PSKY)
function hasCompleteData(f: StockFactors, isFinancial: boolean): boolean {
  const mapping = isFinancial ? PILLAR_FACTORS.financial : PILLAR_FACTORS.nonFinancial;
  for (const [, factors] of Object.entries(mapping)) {
    const valid = factors.filter(({ key }) => f[key] !== null && f[key] !== undefined).length;
    if (valid < 1) return false;
  }
  return true;
}

// ─── Score a group of stocks using a specific factor mapping ─────────────────

type ScoredIntermediate = ScoredStock & { rawPillars: PillarScores; rawValuation: number | null; rawMomentum: number | null };

function scoreGroup(
  stocks: (StockData & { factors: StockFactors; hasOverride?: boolean })[],
  mapping: PillarMapping,
): ScoredIntermediate[] {
  // Compute stats for all factors used by this group
  const allKeys = new Set<keyof StockFactors>();
  for (const factors of Object.values(mapping)) {
    for (const { key } of factors) allKeys.add(key);
  }

  // Winsorize raw factor values at P1/P99 before computing stats (reduces outlier distortion)
  const winsorized: Partial<Record<keyof StockFactors, (number | null)[]>> = {};
  for (const k of allKeys) {
    winsorized[k] = winsorize(stocks.map(s => s.factors[k] as number | null));
  }

  const stats: Partial<Record<keyof StockFactors, { mean: number; std: number } | null>> = {};
  for (const k of allKeys) {
    stats[k] = computeStats(winsorized[k]!);
  }

  return stocks.map((s, idx) => {
    const f = s.factors;
    const z = (k: keyof StockFactors, invert: boolean) => {
      // Use winsorized value for this stock (not raw) to compute z-score
      const wVal = winsorized[k] ? winsorized[k]![idx] : f[k];
      const zv = clamp(zscoreVal(wVal, stats[k] ?? null));
      return zv !== null ? (invert ? -zv : zv) : null;
    };

    // Weighted average of z-scores (factors without a weight default to 1)
    const weightedAvgZ = (factors: { key: keyof StockFactors; invert: boolean; weight?: number }[]) => {
      let wSum = 0, vSum = 0;
      for (const { key, invert, weight } of factors) {
        const zv = z(key, invert);
        if (zv !== null) {
          const w = weight ?? 1;
          vSum += zv * w;
          wSum += w;
        }
      }
      return wSum > 0 ? vSum / wSum : null;
    };

    const profZ = weightedAvgZ(mapping.profitability);
    const growZ = weightedAvgZ(mapping.growth);
    const safeZ = weightedAvgZ(mapping.safety);
    const shrZ = weightedAvgZ(mapping.shareholderReturns);
    const valZ = weightedAvgZ(mapping.valuation);
    const momZ = weightedAvgZ((mapping as typeof PILLAR_FACTORS.nonFinancial).momentum ?? []);

    return {
      symbol: s.symbol, name: s.name, sector: s.sector, industry: s.industry, marketCap: s.marketCap, price: s.price,
      factors: f,
      pillarScores: { profitability: profZ, growth: growZ, safety: safeZ, shareholderReturns: shrZ, momentum: momZ },
      qualityScore: null,
      valuationScore: null,
      momentumScore: null,
      hasOverride: s.hasOverride,
      rawPillars: { profitability: profZ, growth: growZ, safety: safeZ, shareholderReturns: shrZ, momentum: momZ },
      rawValuation: valZ,
      rawMomentum: momZ,
    };
  });
}

// ─── Main Scoring Function ──────────────────────────────────────────────────

/** Optional pillar-config override. Pass a `{ nonFinancial, financial }` shape
 *  to use alternate factor mappings — used by Tier 1 #3 forward-factor
 *  validation (`scripts/research/forward-factor-validation.ts`) to score the
 *  vintages under forward-only, trailing-only, and production configs.
 *  Default behavior (no override) uses the canonical `PILLAR_FACTORS`. */
export function scoreStocks(
  stocks: (StockData & { factors: StockFactors; hasOverride?: boolean })[],
  options?: { pillarFactorsOverride?: { nonFinancial: typeof PILLAR_FACTORS.nonFinancial; financial: typeof PILLAR_FACTORS.financial; reit?: typeof PILLAR_FACTORS.reit } },
): ScoredStock[] {
  const pf = options?.pillarFactorsOverride ?? PILLAR_FACTORS;
  // Apply per-symbol factor patches (data/factor-overrides.json) before peer-group
  // splitting. Use case: BRK's GAAP-EPS-derived multiples are distorted by
  // mark-to-market on its $300B equity book; we null those so they fall to peer
  // median instead of dragging the pillar scores.
  stocks = stocks.map(s => ({ ...s, factors: applyFactorOverrides(s.symbol, s.factors) }));

  // Split into peer groups: financials use bank-specific factors; all others use standard factors
  const financials = stocks.filter(s => isBankLike(s));
  const nonFinancials = stocks.filter(s => !isBankLike(s));

  // Filter each group for data completeness
  const completeFinancials = financials.filter(s => hasCompleteData(s.factors, true));
  const completeNonFin = nonFinancials.filter(s => hasCompleteData(s.factors, false));
  const dropped = stocks.length - completeFinancials.length - completeNonFin.length;
  if (dropped > 0) console.log(`Dropped ${dropped} stocks with incomplete factor data`);

  // Energy / Utility / Insurance use the nonFinancial factor mapping but
  // z-score within their own peer group. P&C insurance is split out of
  // `financial` (bank-style) because the underwriting + float business model
  // doesn't fit NIM/NPL/efficiency — insurance-specific combined-ratio /
  // loss-ratio / expense-ratio / investment-income factors will land in
  // their own flavor in a follow-up. REIT gets its own flavor (pf.reit).
  const energyGroup = completeNonFin.filter(s => isEnergyLike(s));
  const utilityGroup = completeNonFin.filter(s => isUtilityLike(s));
  const reitGroup = completeNonFin.filter(s => isREITLike(s));
  const insuranceGroup = completeNonFin.filter(s => isInsuranceLike(s));
  const techGroup = completeNonFin.filter(s => isTechnologyLike(s));
  const standardGroup = completeNonFin.filter(s => !isEnergyLike(s) && !isUtilityLike(s) && !isREITLike(s) && !isInsuranceLike(s) && !isTechnologyLike(s));

  console.log(`Scoring ${standardGroup.length} standard + ${techGroup.length} technology + ${completeFinancials.length} financial + ${insuranceGroup.length} insurance + ${energyGroup.length} energy + ${utilityGroup.length} utility + ${reitGroup.length} REIT stocks`);

  // Score each group with appropriate factor set. REITs now get their own
  // flavor (2026-05-14) with FFO/NAV/occupancy/NOI-growth/lease-term factors;
  // energy + utility + insurance still on nonFinancial for now.
  const reitFlavor = pf.reit ?? pf.nonFinancial;
  const scoredStandard = scoreGroup(standardGroup, pf.nonFinancial);
  const scoredTech = scoreGroup(techGroup, pf.nonFinancial);
  const scoredFin = scoreGroup(completeFinancials, pf.financial);
  const scoredEnergy = scoreGroup(energyGroup, pf.nonFinancial);
  const scoredUtility = scoreGroup(utilityGroup, pf.nonFinancial);
  const scoredREIT = scoreGroup(reitGroup, reitFlavor);
  const scoredInsurance = scoreGroup(insuranceGroup, pf.nonFinancial);

  // Combine all groups
  const allScored = [...scoredStandard, ...scoredTech, ...scoredFin, ...scoredEnergy, ...scoredUtility, ...scoredREIT, ...scoredInsurance];

  // ── Final: Z-score pillars & quality within peer group ──
  // Pillar weights based on empirical performance analysis (2026-03-30)
  // Weights validated by 2018-2025 backtest:
  // Growth 35%: forward EPS/rev growth are strongest factors (+24-30% spreads, 5/5 consistency)
  // Profitability 20%: ROE/ROA/ROIC work when conditioned on cheap valuation (both regimes)
  // Safety 15%: only current ratio + asset growth consistently predict (most safety factors inverted in large caps)
  // Shareholder Returns 10%: buyback yield is STRONG (+12.3%, 4/5), insider buying untested but theoretically sound
  // 45/20/15/20 is a bias-shrunk vector. Optimizer joint grid on the 9yr cache
  // (2017-2025) found a plateau of Top-10 Sharpe 1.75-1.90 with profitability-heavy /
  // low-Growth vectors. Growth is bumped above the optimizer's raw 10% to account
  // for trailing-growth-only backtest bias, keeping all 4 pillars meaningfully weighted.
  const PILLAR_WEIGHTS = DEFAULT_PILLAR_WEIGHTS_DECIMAL;
  const pillarKeys = QUALITY_PILLAR_KEYS;

  // Build per-peer-group scored arrays
  const groupedScored: Record<PeerGroup, ScoredIntermediate[]> = {
    financial: allScored.filter(s => isBankLike(s)),
    insurance: allScored.filter(s => isInsuranceLike(s)),
    energy: allScored.filter(s => isEnergyLike(s)),
    utility: allScored.filter(s => isUtilityLike(s)),
    reit: allScored.filter(s => isREITLike(s)),
    technology: allScored.filter(s => isTechnologyLike(s)),
    standard: allScored.filter(s => !isBankLike(s) && !isInsuranceLike(s) && !isEnergyLike(s) && !isUtilityLike(s) && !isREITLike(s) && !isTechnologyLike(s)),
  };

  // Compute pillar stats per peer group
  const pillarStats = {} as Record<PeerGroup, Record<string, { mean: number; std: number } | null>>;
  for (const grp of Object.keys(groupedScored) as PeerGroup[]) {
    pillarStats[grp] = {};
    for (const pk of pillarKeys) {
      pillarStats[grp][pk] = computeStats(groupedScored[grp].map(s => s.rawPillars[pk]));
    }
  }

  // Compute valuation stats per peer group
  const valStats = {} as Record<PeerGroup, { mean: number; std: number } | null>;
  for (const grp of Object.keys(groupedScored) as PeerGroup[]) {
    valStats[grp] = computeStats(groupedScored[grp].map(s => s.rawValuation));
  }

  // Compute momentum stats per peer group (added 2026-04-24)
  const momStats = {} as Record<PeerGroup, { mean: number; std: number } | null>;
  for (const grp of Object.keys(groupedScored) as PeerGroup[]) {
    momStats[grp] = computeStats(groupedScored[grp].map(s => s.rawMomentum));
  }

  // Compute quality stats per peer group
  const computeQualityRaw = (group: ScoredIntermediate[], pStats: Record<string, { mean: number; std: number } | null>) =>
    group.map(s => {
      const pz = pillarKeys.map(pk => clamp(zscoreVal(s.rawPillars[pk], pStats[pk] ?? null)));
      let weightSum = 0, valueSum = 0;
      pillarKeys.forEach((pk, i) => {
        if (pz[i] !== null) { const w = PILLAR_WEIGHTS[pk]; valueSum += pz[i]! * w; weightSum += w; }
      });
      return weightSum > 0 ? valueSum / weightSum : null;
    });

  const qualityStats = {} as Record<PeerGroup, { mean: number; std: number } | null>;
  for (const grp of Object.keys(groupedScored) as PeerGroup[]) {
    qualityStats[grp] = computeStats(computeQualityRaw(groupedScored[grp], pillarStats[grp]));
  }

  const finalScored = allScored.map(s => {
    const grp = getPeerGroup(s);
    const pStats = pillarStats[grp];
    const qStats = qualityStats[grp];
    const vStats = valStats[grp];
    const mStats = momStats[grp];

    const pz = pillarKeys.map(pk => clamp(zscoreVal(s.rawPillars[pk], pStats[pk] ?? null)));
    let weightSum = 0, valueSum = 0;
    pillarKeys.forEach((pk, idx) => {
      if (pz[idx] !== null) { const w = PILLAR_WEIGHTS[pk]; valueSum += pz[idx]! * w; weightSum += w; }
    });
    const qualityAvg = weightSum > 0 ? valueSum / weightSum : null;
    const qualityScore = clamp(zscoreVal(qualityAvg, qStats ?? null));
    const valuationScore = clamp(zscoreVal(s.rawValuation, vStats ?? null));
    const momentumScore = clamp(zscoreVal(s.rawMomentum, mStats ?? null));

    const { rawPillars: _rawPillars, rawValuation: _rawValuation, rawMomentum: _rawMomentum, ...rest } = s;
    return {
      ...rest,
      peerGroup: grp,
      pillarScores: {
        profitability: clamp(zscoreVal(s.rawPillars.profitability, pStats.profitability ?? null)),
        growth: clamp(zscoreVal(s.rawPillars.growth, pStats.growth ?? null)),
        safety: clamp(zscoreVal(s.rawPillars.safety, pStats.safety ?? null)),
        shareholderReturns: clamp(zscoreVal(s.rawPillars.shareholderReturns, pStats.shareholderReturns ?? null)),
        momentum: momentumScore,
      },
      qualityScore,
      valuationScore,
      momentumScore,
    };
  });
  return applyQualityAbsoluteLens(finalScored);
}
